import React from 'react';
import AssignmentChip from './AssignmentChip';

/**
 * TimelineAllDaySection Component
 * 
 * Renders the all-day interaction area that sits between the day labels and the time grid.
 * It manages its own horizontal sticky position, vertical scrolling content, and the
 * column resizer handle that allows users to adjust the all-day column width.
 * 
 * @param {Object} props
 * @param {boolean} props.isAllDayCollapsed - Whether the section is hidden
 * @param {boolean} props.isReadOnly - Whether interactions are disabled
 * @param {Function} props.setHoveringAlldayResizer - Hover state setter
 * @param {Function} props.setResizingAllday - Resizing state setter
 * @param {Function} props.setHasManuallyResized - Tracking manual adjustments
 * @param {number} props.alldayWidth - Current raw width value
 * @param {Function} props.setAlldayWidth - Width setter
 * @param {number} props.actualAlldayWidth - Computed width including min/max constraints
 * @param {number} props.totalActualHeight - Total vertical height of all rows combined
 * @param {boolean} props.resizingAllday - Active resizing flag
 * @param {boolean} props.hoveringAlldayResizer - Hovering flag for handle
 * @param {Object} props.allDayLayouts - Layout calculations for all-day chips
 * @param {number[]} props.dayOffsets - Vertical offsets for each day row
 * @param {number[]} props.dayHeights - Individual heights for each day row
 * @param {Object} props.interaction - Current global interaction state
 * @returns {JSX.Element}
 */
export function TimelineAllDaySection({
    isAllDayCollapsed,
    isReadOnly,
    setHoveringAlldayResizer,
    setResizingAllday,
    setHasManuallyResized,
    alldayWidth,
    setAlldayWidth,
    actualAlldayWidth,
    totalActualHeight,
    resizingAllday,
    hoveringAlldayResizer,
    allDayLayouts,
    interaction,
    setInteraction,
    assignmentConflictsMap,
    isConflictDetectionEnabled,
    handleDropAllDay,
    dayOffsets,
    dayHeights,
    days,
    onAssignmentHover,
    onAssignmentHoverEnd,
    onAssignmentClick,
    handleContextMenu,
    handleInteractionStart,
    interactionEnded,
    onConflictHoverEnd
}) {
    return (
        <div className={`timeline-allday-row ${isAllDayCollapsed ? 'collapsed' : ''}`} style={{ height: 0, position: 'relative', zIndex: 1000, borderBottom: 'none' }}>
            {/* 1. Global All-Day Layer (Sticky horizontally, scrolling vertically) */}
            <div className="all-day-global-layer" style={{ 
                position: 'sticky', 
                left: 80, 
                width: actualAlldayWidth, 
                height: totalActualHeight, 
                zIndex: 1000, 
                pointerEvents: 'none',
                overflow: 'visible' // Allow items to be seen
            }}>
                {/* Vertical Column Resizer Handle (Covers Header + Rows) */}
                {!isReadOnly && (
                    <div 
                        className="allday-column-resizer-handle"
                        onMouseEnter={() => setHoveringAlldayResizer(true)}
                        onMouseLeave={() => setHoveringAlldayResizer(false)}
                        onMouseDown={(e) => {
                            if (isReadOnly) return;
                            e.preventDefault();
                            e.stopPropagation();
                            setResizingAllday(true);
                            setHasManuallyResized(true);
                            const startX = e.clientX;
                            const startWidth = alldayWidth;
                            const onMouseMove = (moveEvt) => {
                                const newW = Math.max(32, Math.min(400, startWidth + (moveEvt.clientX - startX)));
                                setAlldayWidth(newW);
                            };
                            const onMouseUp = () => {
                                setTimeout(() => setResizingAllday(false), 100);
                                document.removeEventListener('mousemove', onMouseMove);
                                document.removeEventListener('mouseup', onMouseUp);
                            };
                            document.addEventListener('mousemove', onMouseMove);
                            document.addEventListener('mouseup', onMouseUp);
                        }}
                        style={{
                            position: 'absolute',
                            top: -47, // Reach into header
                            right: -2,
                            width: '4px',
                            height: totalActualHeight + 47,
                            cursor: 'col-resize',
                            zIndex: 1200,
                            pointerEvents: 'auto',
                            backgroundColor: (resizingAllday || hoveringAlldayResizer) ? 'rgba(79, 70, 229, 0.4)' : 'transparent',
                            transition: 'background-color 0.2s'
                        }}
                    />
                )}
                {!isAllDayCollapsed && allDayLayouts.layouts.map(l => {
                    const { event: a, col, actualStartIdx, actualEndIdx } = l;
                    const isGhost = a.isGhost;
                    const isInteracting = String(interaction?.assignment?.id) === String(a.id) && !isGhost && interaction.moved;
                    const isHidden = isInteracting && interaction.moved && !interaction.type.startsWith('resize');
                    
                    let visualTop = dayOffsets[actualStartIdx] + 4;
                    let visualHeight = 0;
                    for (let i = actualStartIdx; i <= actualEndIdx; i++) {
                        visualHeight += dayHeights[i];
                    }
                    
                    // Interaction logic for visual adjustments during move/resize
                    if (isInteracting && interaction.type === 'resize-top') {
                        const origS = new Date(interaction.origS);
                        const currentS = interaction.currentS || origS;
                        origS.setHours(0,0,0,0);
                        currentS.setHours(0,0,0,0);
                        const diffDays = Math.round((origS - currentS) / 86400000);
                        const newStartIdx = Math.max(0, actualStartIdx - diffDays);
                        visualTop = dayOffsets[newStartIdx] + 4;
                        let newHeight = 0;
                        for (let i = newStartIdx; i <= actualEndIdx; i++) {
                            newHeight += dayHeights[i];
                        }
                        visualHeight = newHeight;
                    } else if (isInteracting && interaction.type === 'resize-bottom') {
                        const origE = new Date(interaction.origE);
                        const currentE = interaction.currentE || origE;
                        origE.setHours(0,0,0,0);
                        currentE.setHours(0,0,0,0);
                        const diffDays = Math.round((currentE - origE) / 86400000);
                        const newEndIdx = Math.max(actualStartIdx, actualEndIdx + diffDays);
                        let newHeight = 0;
                        for (let i = actualStartIdx; i <= Math.min(days.length - 1, newEndIdx); i++) {
                            newHeight += dayHeights[i];
                        }
                        visualHeight = newHeight;
                    } else if (isInteracting && interaction.type === 'move-allday') {
                        const origS = new Date(interaction.origS);
                        const currentS = interaction.currentS || origS;
                        origS.setHours(0,0,0,0);
                        currentS.setHours(0,0,0,0);
                        const diffDays = Math.round((currentS - origS) / 86400000);
                        const newStartIdx = Math.max(0, Math.min(days.length - 1, actualStartIdx + diffDays));
                        visualTop = dayOffsets[newStartIdx] + 4;
                    }

                    const barHeight = visualHeight - 8;
                    const barWidth = 24;
                    const leftOffset = col * (barWidth + 4); 

                    return (
                        <AssignmentChip
                            key={a.id}
                            assignment={a}
                            mode="timeline-allday"
                            hasConflict={assignmentConflictsMap[a.id]?.hasConflict}
                            isConflictDetectionEnabled={isConflictDetectionEnabled}
                            interaction={interaction}
                            onDrop={handleDropAllDay}
                            style={{
                                position: 'absolute',
                                top: visualTop,
                                height: barHeight,
                                left: leftOffset,
                                width: barWidth,
                                zIndex: (isInteracting || isGhost) ? 1100 : 140,
                                opacity: isHidden ? 0 : 1,
                                pointerEvents: isHidden ? 'none' : (isGhost ? 'none' : 'auto'),
                            }}
                            onMouseEnter={(e) => onAssignmentHover?.(a, e)}
                            onMouseLeave={onAssignmentHoverEnd}
                            onMouseDown={(e, resizeType) => {
                                if (isReadOnly) return;
                                if (resizeType) {
                                    handleInteractionStart(e, resizeType, a);
                                } else {
                                    onAssignmentHoverEnd?.();
                                    onConflictHoverEnd?.();
                                    handleInteractionStart(e, 'move-allday', a);
                                }
                            }}
                            onClick={(e) => {
                                if (interactionEnded) return;
                                e.stopPropagation();
                                e.preventDefault();
                                setTimeout(() => onAssignmentClick(a), 0);
                            }}
                            onContextMenu={(e) => { e.stopPropagation(); if (!isReadOnly) handleContextMenu(e, a); }}
                            onDragStart={(e) => {
                                if (isReadOnly) { e.preventDefault(); return; }
                                const img = new Image();
                                img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
                                e.dataTransfer.setDragImage(img, 0, 0);
                                const data = { type: 'assignment', assignment: a };
                                e.dataTransfer.setData('application/json', JSON.stringify(data));
                                e.dataTransfer.effectAllowed = 'move';
                            }}
                            onDragEnd={() => setInteraction(null)}
                        />
                    );
                })}
            </div>
        </div>
    );
}
